import React, { useCallback, useEffect, useState } from "react";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { Box, Grid, Button, Dialog } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CommonLoader from "../../sharedComponents/CommonLoader/CommonLoader";
import AddClient from "../../components/Client/AddClient";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import {
  deleteClient,
  getClient,
  updateClient,
} from "../../services/client.service";
import { debounce } from "../../utils/debounce";
import PermissionGate from "../../components/Permission/PermissionGate";
import { usePermissions } from "../../context/PermissionProvider";

const Client = () => {
  const [data, setData] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [tableTotalCount, setTableTotalCount] = useState(0);
  const { canAdd, canEdit, canDelete } = usePermissions();
  const [queryParam, setQueryParam] = useState({
    search: "",
    page: 1,
    limit: 10,
    sortType: "",
    sort: "",
  });

  const getClientData = async () => {
    try {
      const encryptedQueryParam = {
        search: queryParam.search,
        page: queryParam.page,
        limit: queryParam.limit,
        sortType: queryParam.sortType,
        sort: queryParam.sort,
      };

      const res = await getClient(encryptedQueryParam);
      const modifiedData =
        res?.data.data.map((e) => ({
          ...e,
          projectName: e?.project?.map((el) => el?.name),
          // projectName: e?.project?.map((el) => el?.name).join(",")
        })) || [];
      setTableTotalCount(res?.data?.totalCount || 0);
      setData(modifiedData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getClientData();
  }, [queryParam.search, queryParam.page, queryParam.limit, queryParam.sort]);

  const handleAdd = () => {
    setOpenDrawer(true);
    setSelectedData({});
  };

  const handleView = (e) => {
    e.isView = true;
    setOpenDrawer(true);
    setSelectedData(e);
  };

  const handleEdit = (e) => {
    e.isView = false;
    setOpenDrawer(true);
    setSelectedData(e);
  };

  const handleDelete = async (e) => {
    try {
      const res = await deleteClient(e._id);
      ToastError(res.message);
      getClientData();
    } catch (error) {
      console.log(error);
    }
  };

  const debouncedHandleSearch = useCallback(
    debounce((searchText) => {
      setQueryParam((queryParams) => ({
        ...queryParams,
        search: searchText,
      }));
    }, 300),
    []
  );

  const handleSearch = (searchText) => {
    debouncedHandleSearch(searchText);
  };

  const tableOutputEvent = useCallback((params) => {
    setQueryParam((queryParams) => ({
      ...queryParams,
      ...params,
    }));
  }, []);

  const handleToggle = async (e) => {
    try {
      const formData = new FormData();
      const payload = {
        isEnabled: !e.isEnabled,
      };
      const { file, ...bodyData } = e;
      formData.append("bodyData", JSON.stringify(payload));

      if (file) {
        formData.append("file", file);
      }

      const res = await updateClient(e._id, formData);
      if (payload.isEnabled) {
        ToastSuccess(res?.data?.message);
      } else {
        if (res) {
          ToastError(res?.data?.message);
        } else {
          ToastError("Client Deactivated");
        }
      }
      setData((prevData) =>
        prevData.map((item) =>
          item._id === e._id ? { ...item, isEnabled: payload.isEnabled } : item
        )
      );
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "mobileNo", label: "Mobile No" },
    { id: "projectName", label: "Project" },
  ];

  const tableConfig = {
    isMore: true,
    isClient: true,
    isAction: true,
    isView: true,
    isEdit: canEdit("client"),
    isDelete: canDelete("client"),
    isSearch: false,
    isAddNew: false,
    isToggle: false,
    isAdmin: true,
  };
  return (
    <div className="mainPageRenderingContainer">
      <CommonLoader />
      <Box sx={{ height: "fit-content" }}>
        <Grid container spacing={2} sx={{ justifyContent: "space-between" }}>
          <Grid item xs={12} sm={2.5}>
            <Box className="SearchInputBoxContainer">
              <SearchOutlinedIcon className="Search_Icon" />
              <input
                className="SearchInputBox"
                placeholder="Search here"
                onChange={(e) => handleSearch(e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} />
          <Grid item xs={12} sm={1.5}>
            <Button
              startIcon={<AddOutlinedIcon />}
              onClick={() => handleAdd()}
              className="Add-Button w-100"
            >
              Add New
            </Button>
          </Grid>
          {/* <Grid item xs={12} sm={2.5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DatePicker openTo="year" className="datepicker w-100" />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Grid> */}
          <Grid
            item
            xs={12}
            sm={1.5}
            sx={{ display: tableConfig.isAddNew ? "block" : "none" }}
          >
            <PermissionGate module="client" action="add">
              <Button
                startIcon={<AddOutlinedIcon />}
                onClick={() => handleAdd()}
                className="Add-Button w-100"
              >
              Add New
            </Button>
            </PermissionGate>
          </Grid>
        </Grid>
      </Box>
      <MasterCommonTable
        tableConfig={tableConfig}
        data={data}
        columns={columns}
        handleEdit={handleEdit}
        handleToggle={handleToggle}
        handleView={handleView}
        handleDelete={handleDelete}
        isServerSide={true}
        tableTotalCount={tableTotalCount}
        tableOutputEvent={tableOutputEvent}
      />
      <Dialog
        open={openDrawer}
        PaperProps={{
          style: {
            width: "100%",
            height: "fit-content",
          },
        }}
        fullWidth
        maxWidth="md"
      >
        {openDrawer ? (
          <AddClient
            selectedData={selectedData}
            setOpenDrawer={setOpenDrawer}
            formType={
              selectedData.isView ? "view" : selectedData._id ? "edit" : "add"
            }
            getData={getClientData}
          />
        ) : null}
      </Dialog>
    </div>
  );
};
export default Client;
